import { useCallback } from 'react'

import { useMatch, useSearchParams } from 'react-router-dom'

import { relativeDatesFactory } from '../../../Filters/valuePickers/DateValuePicker.js'
import { useI18n } from '../../../i18n/index.js'
import {
  SMART_SEARCH_RESULTS_FETCH_MORE_PARAM,
  SmartSearchEntityType,
  SmartSearchTabsType,
} from '../../index.js'

import {
  SEARCH_PAGE_QUERY_PARAMS,
  SMART_SEARCH_DEFAULT_VALUES,
} from './useSmartSearchUrlParams.constants.js'
import {
  isValidDateParam,
  isValidViewParam,
  isValidTypeParam,
} from './useSmartSearchUrlParams.utils.js'

/**
 * a hook to get and set the url params for the smart search page
 * @returns an object with the values and setters for the url params
 */
export const useSmartSearchUrlParams = () => {
  const intl = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()

  const relativeDates = relativeDatesFactory(intl)

  // Get values from URL
  const query =
    searchParams.get(SEARCH_PAGE_QUERY_PARAMS.QUERY) ||
    SMART_SEARCH_DEFAULT_VALUES.query
  const tagsId =
    searchParams.getAll(SEARCH_PAGE_QUERY_PARAMS.TAGS_ID) ||
    SMART_SEARCH_DEFAULT_VALUES.tagsId
  const spaceIds =
    searchParams.getAll(SEARCH_PAGE_QUERY_PARAMS.SPACE_IDS) ||
    SMART_SEARCH_DEFAULT_VALUES.spaceIds
  const rawType = searchParams.get(SEARCH_PAGE_QUERY_PARAMS.TYPE)

  // Handle legacy official_posts type by converting to posts + expanded
  const normalizedType =
    rawType === SmartSearchEntityType.OFFICIAL_POSTS
      ? SmartSearchEntityType.POSTS
      : rawType

  const type = isValidTypeParam(normalizedType)
    ? normalizedType
    : SMART_SEARCH_DEFAULT_VALUES.type

  // Handle expanded parameter with legacy support
  const rawExpanded = searchParams.get(SEARCH_PAGE_QUERY_PARAMS.EXPANDED)
  const expandedFromLegacyType =
    rawType === SmartSearchEntityType.OFFICIAL_POSTS
      ? SmartSearchEntityType.OFFICIAL_POSTS
      : null

  const expanded = isValidTypeParam(rawExpanded)
    ? rawExpanded
    : expandedFromLegacyType || SMART_SEARCH_DEFAULT_VALUES.expanded

  const rawDate = searchParams.get(SEARCH_PAGE_QUERY_PARAMS.DATE)
  const date =
    rawDate && isValidDateParam(rawDate, relativeDates)
      ? rawDate
      : SMART_SEARCH_DEFAULT_VALUES.date
  const rawView = searchParams.get(SEARCH_PAGE_QUERY_PARAMS.VIEW)
  const view = isValidViewParam(rawView)
    ? rawView
    : SMART_SEARCH_DEFAULT_VALUES.view
  const fetchMore =
    searchParams.get(SMART_SEARCH_RESULTS_FETCH_MORE_PARAM) === 'true'

  /**
   * Set URL params in bunch or individually in a single call
   *     if any of the params are null, they will be deleted from the url
   *     if any of the params are undefined, they will be left unchanged
   *     if any of the params are not provided, they will be left unchanged
   */
  const setParams = useCallback(
    ({
      newQuery,
      newType,
      newTagsId,
      newDate,
      newView,
      newFetchMore,
      newSpaceIds,
      newExpanded,
    }: {
      newQuery?: string | null
      newType?: SmartSearchEntityType | null
      newTagsId?: string[] | null
      newDate?: number | string | null
      newView?: SmartSearchTabsType | null
      newFetchMore?: boolean | null
      newSpaceIds?: string[] | null
      newExpanded?: SmartSearchEntityType | null
    }) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)

          // Handle query
          if (newQuery === null) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.QUERY)
          } else if (newQuery !== undefined) {
            params.set(SEARCH_PAGE_QUERY_PARAMS.QUERY, newQuery)
          }

          // Handle type
          if (newType === null) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.TYPE)
          } else if (newType !== undefined) {
            params.set(SEARCH_PAGE_QUERY_PARAMS.TYPE, newType)
          }

          // Handle tagsId
          if (newTagsId !== undefined) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.TAGS_ID)
            newTagsId?.forEach(tagId => {
              params.append(SEARCH_PAGE_QUERY_PARAMS.TAGS_ID, tagId)
            })
          }

          // Handle date
          if (newDate === null) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.DATE)
          } else if (newDate !== undefined) {
            params.set(SEARCH_PAGE_QUERY_PARAMS.DATE, newDate.toString())
          }

          // Handle view
          if (newView === null) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.VIEW)
          } else if (newView !== undefined) {
            params.set(SEARCH_PAGE_QUERY_PARAMS.VIEW, newView)
          }

          // Handle fetchMore
          if (!newFetchMore) {
            params.delete(SMART_SEARCH_RESULTS_FETCH_MORE_PARAM)
          } else if (newFetchMore === true) {
            params.set(SMART_SEARCH_RESULTS_FETCH_MORE_PARAM, 'true')
          }

          // Handle spaceIds
          if (newSpaceIds !== undefined) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.SPACE_IDS)
            newSpaceIds?.forEach(spaceId => {
              params.append(SEARCH_PAGE_QUERY_PARAMS.SPACE_IDS, spaceId)
            })
          }

          // Handle expanded
          if (newExpanded === null) {
            params.delete(SEARCH_PAGE_QUERY_PARAMS.EXPANDED)
          } else if (newExpanded !== undefined) {
            params.set(SEARCH_PAGE_QUERY_PARAMS.EXPANDED, newExpanded)
          }

          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const isInSearchPage = useMatch('/search')

  return {
    // Values
    query,
    tagsId,
    spaceIds,
    type,
    date,
    view,
    fetchMore,
    expanded,
    isInSearchPage,
    // Setters
    setParams,
  }
}
